import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, CheckCircle2, FileText, Globe, Calculator, Download, Eye, Zap } from 'lucide-react';

const tableOfContents = [
  { id: 'what-is-invoice', label: 'What Is an Invoice?' },
  { id: 'create-invoice', label: 'How to Create a Free Invoice' },
  { id: 'invoice-elements', label: 'Required Invoice Elements' },
  { id: 'taxes', label: 'How to Add VAT, GST & SST Taxes' },
  { id: 'currencies', label: 'Multi-Currency Invoicing' },
  { id: 'quotation-to-invoice', label: 'Quotation to Invoice Conversion' },
  { id: 'download', label: 'Download as PDF or Word' },
  { id: 'payment-terms', label: 'Invoice Payment Terms Guide' },
  { id: 'tracking', label: 'Tracking Invoice Opens (Pro)' },
  { id: 'tips', label: 'Pro Tips to Get Paid Faster' },
];

const guideStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to Create a Free Professional Invoice Online',
  description:
    'Step-by-step guide on how to create a free professional invoice using Quick Free Invoice Tool. Covers invoice elements, VAT/GST taxes, multi-currency billing, PDF download, and more.',
  url: 'https://quickfreeinvoicetool.com/guide',
  step: [
    { '@type': 'HowToStep', name: 'Open the Invoice Builder', text: 'Visit quickfreeinvoicetool.com and click Create Invoice.' },
    { '@type': 'HowToStep', name: 'Add Your Business Details', text: 'Enter your business name, logo, address, phone, email, and tax ID number.' },
    { '@type': 'HowToStep', name: 'Add Client Details', text: 'Enter your client\'s name, company, and billing address.' },
    { '@type': 'HowToStep', name: 'Add Invoice Number and Dates', text: 'Set your invoice number, invoice date, and payment due date (e.g. Net 30).' },
    { '@type': 'HowToStep', name: 'Add Line Items', text: 'List each product or service with description, quantity, unit price, and any per-line tax.' },
    { '@type': 'HowToStep', name: 'Add Taxes', text: 'Enter your VAT, GST, SST, or sales tax rate. The tool calculates totals automatically.' },
    { '@type': 'HowToStep', name: 'Download or Share', text: 'Click Download PDF or Download Word to save your invoice. Share via link or email.' },
  ],
  tool: [
    { '@type': 'HowToTool', name: 'Quick Free Invoice Tool (free online invoice generator)' },
  ],
};

export default function Guide() {
  const [activeSection] = useState('');

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Free Invoice Guide — How to Create a Professional Invoice Online | Quick Free Invoice Tool"
        description="The complete free invoice guide. Learn how to create professional invoices online, add VAT/GST taxes, download PDFs, convert quotations to invoices, set payment terms, and get paid faster. Step-by-step for freelancers and small businesses."
        keywords="how to create invoice, free invoice guide, invoice tutorial, vat invoice guide, gst invoice tutorial, invoice payment terms, quotation to invoice, pdf invoice download, invoice for freelancers, invoice for small business, invoice elements, professional invoice guide"
        canonical="https://quickfreeinvoicetool.com/guide"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(guideStructuredData) }}
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-700 text-white py-20">
        <div className="container mx-auto px-4 text-center max-w-4xl">
          <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider rounded-full">
            Complete Invoice Guide
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold mb-6 leading-tight">
            Free Invoice Guide — How to Create a Professional Invoice Online in Under 60 Seconds
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto leading-relaxed">
            The definitive guide for freelancers and small businesses. Learn every step — from invoice elements and tax calculations to PDF downloads and getting paid faster.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/">
              <Button size="lg" className="bg-white text-indigo-600 hover:bg-indigo-50 font-bold">
                Create a Free Invoice Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Layout: TOC + Content */}
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="flex gap-12">
          {/* Sticky TOC */}
          <aside className="hidden lg:block w-64 shrink-0">
            <div className="sticky top-6">
              <div className="font-semibold text-xs uppercase tracking-wider text-slate-500 mb-3">On This Page</div>
              <nav className="space-y-1">
                {tableOfContents.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className={`block text-sm px-3 py-1.5 rounded-md transition-colors ${
                      activeSection === item.id
                        ? 'bg-indigo-50 text-indigo-700 font-medium'
                        : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
              <div className="mt-8 pt-6 border-t border-slate-200">
                <Link to="/">
                  <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs">
                    Create Free Invoice
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                  </Button>
                </Link>
              </div>
            </div>
          </aside>

          {/* Main Guide Content */}
          <main className="flex-1 prose prose-slate max-w-none">

            {/* Section 1 */}
            <section id="what-is-invoice" className="mb-16 scroll-mt-6">
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <FileText className="h-7 w-7 text-indigo-500 shrink-0" />
                What Is an Invoice? Definition, Purpose & Legal Requirements
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                An <strong>invoice</strong> is a formal billing document sent by a seller (you) to a buyer (your client) requesting payment for goods delivered or services rendered. Unlike a receipt — which confirms payment already made — an invoice is a <em>request for payment</em> that creates a legal payment obligation.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                <Card className="border-indigo-100 bg-indigo-50">
                  <CardContent className="pt-5">
                    <h3 className="font-bold text-slate-900 mb-2 text-sm">Invoice (request for payment)</h3>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>✅ Sent <strong>before</strong> payment</li>
                      <li>✅ Includes payment due date</li>
                      <li>✅ Creates legal payment obligation</li>
                      <li>✅ Used for tax reporting (VAT/GST)</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-emerald-100 bg-emerald-50">
                  <CardContent className="pt-5">
                    <h3 className="font-bold text-slate-900 mb-2 text-sm">Receipt (proof of payment)</h3>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>✅ Issued <strong>after</strong> payment received</li>
                      <li>✅ Shows payment method used</li>
                      <li>✅ Confirms transaction is complete</li>
                      <li>✅ Used for expense reimbursement</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              <p className="text-slate-600 leading-relaxed">
                A professional invoice protects both parties: it sets clear payment expectations, provides a paper trail for accounting, and is required by most tax authorities (HMRC, ATO, IRAS, LHDN, IRS) for VAT/GST compliance.
              </p>
            </section>

            {/* Section 2 */}
            <section id="create-invoice" className="mb-16 scroll-mt-6">
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Zap className="h-7 w-7 text-amber-500 shrink-0" />
                How to Create a Free Invoice Online — Step-by-Step Guide
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                With Quick Free Invoice Tool, you can create a professional invoice in under 60 seconds. No account required. Here is the exact process:
              </p>
              <div className="space-y-4">
                {[
                  {
                    step: '1',
                    title: 'Open the Free Invoice Builder',
                    body: 'Go to quickfreeinvoicetool.com and click "Create Invoice". The invoice builder opens instantly — no loading screens, no sign-up forms.',
                  },
                  {
                    step: '2',
                    title: 'Enter Your Business Details',
                    body: 'Fill in your business name, logo (optional), physical address, phone number, email, and tax registration number (VAT/GST number if applicable).',
                  },
                  {
                    step: '3',
                    title: 'Add Your Client\'s Billing Information',
                    body: 'Enter your client\'s full name or company name, billing address, and email. Double-check the details — errors here cause payment delays.',
                  },
                  {
                    step: '4',
                    title: 'Set Invoice Number, Date & Due Date',
                    body: 'Use a sequential invoice number (e.g. INV-2026-001). Set today\'s date as the invoice date and add a due date to enforce payment terms (Net 15, Net 30, etc.).',
                  },
                  {
                    step: '5',
                    title: 'Add Line Items for Products or Services',
                    body: 'Click "Add Item" for each product, service, or hourly block. Enter a clear description, quantity, and unit price. The tool calculates line totals automatically.',
                  },
                  {
                    step: '6',
                    title: 'Add Taxes (VAT, GST, SST, or Custom)',
                    body: 'Enter your applicable tax rate. The invoice generator calculates the tax amount and grand total instantly. You can add multiple tax lines for mixed-rate supplies.',
                  },
                  {
                    step: '7',
                    title: 'Add Payment Instructions & Notes',
                    body: 'Include your bank account details, preferred payment methods (bank transfer, PayPal, card), or any additional notes for the client.',
                  },
                  {
                    step: '8',
                    title: 'Download PDF or Word & Send to Client',
                    body: 'Click "Download PDF" for a print-ready invoice or "Download Word" for an editable file. Share the invoice link or attach the file in your email.',
                  },
                ].map((s) => (
                  <div key={s.step} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-indigo-600 text-white font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 mb-1">{s.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 3 */}
            <section id="invoice-elements" className="mb-16 scroll-mt-6">
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <CheckCircle2 className="h-7 w-7 text-emerald-500 shrink-0" />
                Required Invoice Elements — What Every Professional Invoice Must Include
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Tax authorities in most countries specify exactly what information must appear on a valid invoice. Missing elements can make an invoice non-compliant and delay your tax refund or your client's input tax credit claim.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'The word "INVOICE" clearly displayed',
                  'Unique sequential invoice number',
                  'Invoice date (date of issue)',
                  'Payment due date and terms (Net 30 etc.)',
                  'Your full business name and address',
                  'Your tax registration number (VAT/GST No.)',
                  'Client\'s full name and billing address',
                  'Client\'s VAT/GST number (for B2B)',
                  'Clear description of goods or services',
                  'Quantity and unit price for each item',
                  'Subtotal before tax',
                  'Tax rate applied and tax amount',
                  'Grand total amount due (including tax)',
                  'Accepted payment methods and bank details',
                  'Currency of payment',
                  'Your contact information',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                    {item}
                  </div>
                ))}
              </div>
            </section>

            {/* Section 4 */}
            <section id="taxes" className="mb-16 scroll-mt-6">
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Calculator className="h-7 w-7 text-purple-500 shrink-0" />
                How to Add VAT, GST & SST Taxes to Your Invoice
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Quick Free Invoice Tool supports all major tax systems. Here is how to apply taxes correctly based on your country:
              </p>
              <div className="space-y-4">
                {[
                  {
                    tax: 'VAT (Value Added Tax) — UK & Europe',
                    color: 'border-blue-300 bg-blue-50',
                    details: [
                      'UK standard rate: 20% | Reduced rate: 5% | Zero rate: 0%',
                      'EU rates vary: Germany 19%, France 20%, Netherlands 21%',
                      'Display your VAT registration number on every invoice',
                      'Both subtotal (ex-VAT) and VAT amount must show separately',
                    ],
                  },
                  {
                    tax: 'GST (Goods and Services Tax) — Australia, Canada, India, Singapore',
                    color: 'border-emerald-300 bg-emerald-50',
                    details: [
                      'Australia: 10% | Canada federal GST: 5% | Singapore: 9%',
                      'India: tiered at 5%, 12%, 18%, 28% depending on supply',
                      'Show GST separately from the subtotal on your invoice',
                      'Include your GST registration number if registered',
                    ],
                  },
                  {
                    tax: 'SST (Sales and Service Tax) — Malaysia',
                    color: 'border-orange-300 bg-orange-50',
                    details: [
                      'Sales Tax: 5% or 10% (depending on goods category)',
                      'Service Tax: 6% on prescribed services',
                      'SST is not a credit system — charged at point of sale',
                      'Include your SST registration number on taxable invoices',
                    ],
                  },
                  {
                    tax: 'US Sales Tax — United States',
                    color: 'border-rose-300 bg-rose-50',
                    details: [
                      'Rate varies by state, county, and city (0%–10%+)',
                      'Only charged at point of retail sale to end consumer',
                      'B2B sales are often exempt with a reseller certificate',
                      'Use a custom tax rate in Quick Free Invoice Tool matching your state',
                    ],
                  },
                ].map((t) => (
                  <div key={t.tax} className={`border-l-4 rounded-r-lg p-4 ${t.color}`}>
                    <h3 className="font-bold text-slate-900 mb-2 text-sm">{t.tax}</h3>
                    <ul className="space-y-1">
                      {t.details.map((d) => (
                        <li key={d} className="text-xs text-slate-600">• {d}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 5 */}
            <section id="currencies" className="mb-16 scroll-mt-6">
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Globe className="h-7 w-7 text-cyan-500 shrink-0" />
                Multi-Currency Invoicing — How to Bill International Clients
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Quick Free Invoice Tool supports 170+ currencies. When billing international clients, follow these best practices:
              </p>
              <div className="space-y-3">
                {[
                  { tip: 'Always state the invoice currency explicitly', desc: 'Include the 3-letter ISO currency code (USD, EUR, GBP, AUD) next to all amounts to avoid disputes.' },
                  { tip: 'Agree on the payment currency upfront', desc: 'Decide before work starts whether you\'ll bill in your local currency or the client\'s currency — and document it in your contract.' },
                  { tip: 'Add extra days for international payments', desc: 'SWIFT bank transfers typically take 3–5 business days. If your standard terms are Net 30, consider Net 35–40 for overseas clients.' },
                  { tip: 'Note the exchange rate on the invoice', desc: 'If billing in a foreign currency, note the exchange rate used on the invoice date for your own accounting records.' },
                  { tip: 'Check import taxes and duties', desc: 'For physical goods, include the correct HS codes and note whether prices are inclusive or exclusive of import duties in the destination country.' },
                ].map((item) => (
                  <div key={item.tip} className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <div className="font-semibold text-slate-900 text-sm mb-1">{item.tip}</div>
                    <div className="text-xs text-slate-500 leading-relaxed">{item.desc}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 6 */}
            <section id="quotation-to-invoice" className="mb-16 scroll-mt-6">
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <FileText className="h-7 w-7 text-rose-500 shrink-0" />
                How to Convert a Quotation or Estimate Into an Invoice
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Quotations and estimates are used <em>before</em> work begins to outline the expected scope and cost. Once a client approves the quotation, you convert it to a final invoice. Quick Free Invoice Tool makes this a one-click process.
              </p>
              <div className="space-y-3">
                {[
                  { n: '1', t: 'Create a Quotation', d: 'Use the Quotation Builder to create your price quote. Include all expected line items, estimated hours, materials, and applicable taxes.' },
                  { n: '2', t: 'Share with the Client', d: 'Download the quotation as PDF or share the client-view link. The client can review the quote without needing an account.' },
                  { n: '3', t: 'Client Approves — Convert to Invoice', d: 'Once the client approves, open the quotation and click "Convert to Invoice". All line items carry over automatically.' },
                  { n: '4', t: 'Update Invoice Date & Number', d: 'The system generates a new invoice number. Confirm the invoice date and payment due date, then download or send.' },
                ].map((s) => (
                  <div key={s.n} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-rose-500 text-white font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">{s.n}</div>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{s.t}</div>
                      <div className="text-xs text-slate-500 leading-relaxed mt-0.5">{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 7 */}
            <section id="download" className="mb-16 scroll-mt-6">
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Download className="h-7 w-7 text-indigo-500 shrink-0" />
                How to Download Your Invoice as PDF or Word (.docx)
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Quick Free Invoice Tool provides two free export options:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-indigo-200">
                  <CardContent className="pt-5">
                    <h3 className="font-bold text-slate-900 mb-3 text-sm">PDF Invoice Download</h3>
                    <ul className="space-y-1.5 text-xs text-slate-600">
                      <li>✅ Click "Download PDF" in the builder</li>
                      <li>✅ Pixel-perfect layout — no editing needed</li>
                      <li>✅ Best for emailing to clients</li>
                      <li>✅ Works on all email clients</li>
                      <li>✅ Print-ready (A4 & Letter size)</li>
                      <li>✅ No watermarks, no fees</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="border-blue-200">
                  <CardContent className="pt-5">
                    <h3 className="font-bold text-slate-900 mb-3 text-sm">Word (.docx) Invoice Download</h3>
                    <ul className="space-y-1.5 text-xs text-slate-600">
                      <li>✅ Click "Download Word" in the builder</li>
                      <li>✅ Fully editable in Microsoft Word</li>
                      <li>✅ Useful for accounting software imports</li>
                      <li>✅ Easy to convert to PDF in Word</li>
                      <li>✅ Good for internal record keeping</li>
                      <li>✅ No watermarks, no fees</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* Section 8 */}
            <section id="payment-terms" className="mb-16 scroll-mt-6">
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Calculator className="h-7 w-7 text-emerald-500 shrink-0" />
                Invoice Payment Terms Explained — Net 30, Due on Receipt & More
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Payment terms define when your client must pay. They should appear prominently on every invoice. Here is what each standard term means:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="text-left px-4 py-2 font-semibold text-slate-900 border border-slate-200">Term</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-900 border border-slate-200">Meaning</th>
                      <th className="text-left px-4 py-2 font-semibold text-slate-900 border border-slate-200">Best For</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600">
                    {[
                      ['Due on Receipt', 'Payment expected immediately', 'New clients, small amounts'],
                      ['Net 15', 'Payment due within 15 days', 'Established clients, short projects'],
                      ['Net 30', 'Payment due within 30 days', 'Standard B2B invoicing'],
                      ['Net 60', 'Payment due within 60 days', 'Large clients, long projects'],
                      ['2/10 Net 30', '2% discount if paid within 10 days', 'Incentivizing early payment'],
                      ['EOM', 'Payment due at end of month', 'Retainer agreements'],
                      ['COD', 'Payment on delivery of goods', 'Physical product sales'],
                      ['PIA', 'Payment in advance before work starts', 'High-risk or new clients'],
                    ].map(([term, meaning, best]) => (
                      <tr key={term} className="border-b border-slate-200 hover:bg-slate-50">
                        <td className="px-4 py-2 border border-slate-200 font-medium">{term}</td>
                        <td className="px-4 py-2 border border-slate-200">{meaning}</td>
                        <td className="px-4 py-2 border border-slate-200 text-slate-500">{best}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 9 */}
            <section id="tracking" className="mb-16 scroll-mt-6">
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Eye className="h-7 w-7 text-orange-500 shrink-0" />
                How to Track When Clients Open Your Invoice (Pro Feature)
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Quick Free Invoice Tool Pro gives you real-time read receipts for invoices and quotations. Here is how it works:
              </p>
              <div className="space-y-3">
                {[
                  'Share your invoice using the built-in shareable client-view link',
                  'When the client opens the link, you receive an instant notification',
                  'The dashboard shows exact open time, client IP location, and device type',
                  'View outstanding, overdue, and paid invoices on the analytics dashboard',
                  'Get automatic reminders when invoices reach their due date',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2 text-sm text-slate-700">
                    <Eye className="h-4 w-4 text-orange-400 shrink-0 mt-0.5" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <Link to="/pricing">
                  <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white font-bold">
                    Upgrade to Pro for Invoice Tracking
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </section>

            {/* Section 10 */}
            <section id="tips" className="mb-16 scroll-mt-6">
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                <Zap className="h-7 w-7 text-yellow-500 shrink-0" />
                10 Pro Tips to Get Paid Faster — Invoice Best Practices for Freelancers
              </h2>
              <div className="space-y-4">
                {[
                  { n: 1, t: 'Send invoices the same day work is complete', d: 'Delayed invoices lead to delayed payments. Invoice within 24 hours of finishing each project or milestone.' },
                  { n: 2, t: 'Use short payment terms (Net 15 instead of Net 30)', d: 'Research shows shorter payment windows result in faster average payment times. Start with Net 15 for new clients.' },
                  { n: 3, t: 'Offer multiple payment methods', d: 'List bank transfer, PayPal, card, and any other method you accept. Every barrier to payment extends the time to get paid.' },
                  { n: 4, t: 'Send a payment reminder 3–5 days before the due date', d: 'A polite reminder before the due date catches invoices that have been forgotten — without any awkwardness.' },
                  { n: 5, t: 'Add late payment fees to your invoice', d: 'State a 1.5%–2% monthly interest charge for overdue amounts. This motivates timely payment and protects you legally.' },
                  { n: 6, t: 'Use sequential invoice numbers', d: 'Professional numbering (INV-2026-001, INV-2026-002) builds trust and makes your invoices easy to trace in disputes.' },
                  { n: 7, t: 'Include your bank details on every invoice', d: 'Never make a client hunt for your payment details. List your full bank account info or a payment link prominently.' },
                  { n: 8, t: 'Add a thank-you note to every invoice', d: 'A simple "Thank you for your business" at the bottom makes your invoice feel human and increases client goodwill.' },
                  { n: 9, t: 'Require a deposit before starting large projects', d: 'A 25–50% deposit secures your income, filters serious clients, and reduces non-payment risk on large scopes.' },
                  { n: 10, t: 'Use invoice tracking (Pro) to know when clients have seen your invoice', d: 'With read receipts, you know exactly when to follow up — eliminating "I never received it" excuses.' },
                ].map((tip) => (
                  <div key={tip.n} className="flex gap-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
                    <div className="w-7 h-7 rounded-full bg-yellow-400 text-slate-900 font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                      {tip.n}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{tip.t}</div>
                      <div className="text-xs text-slate-500 leading-relaxed mt-0.5">{tip.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-center text-white">
              <h2 className="font-heading text-2xl font-bold mb-3">Ready to Create Your First Free Invoice?</h2>
              <p className="text-indigo-100 mb-6 text-sm">
                Use Quick Free Invoice Tool to create professional invoices and quotations in under 60 seconds — free, forever.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/">
                  <Button size="lg" className="bg-white text-indigo-600 hover:bg-indigo-50 font-bold">
                    Create Free Invoice Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/faq">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    View FAQ
                  </Button>
                </Link>
              </div>
            </div>

          </main>
        </div>
      </div>
    </div>
  );
}
